import React from "react";
import { Alert, AlertTitle, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { useCustomListViewHooks } from "./CustomListView.hooks";
import { useTranslation } from "../../localization/useTranslation";
import { ContentType, CustomListType } from "../CustomLists.types";
import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalInformationTooltip,
    DotLegalPageHeader,
    DotLegalPagination,
    DotLegalPrettyScrollbar,
    DotLegalSearchField,
    useDotLegalTableStyles,
} from "@dotlegal/dotlegal-ui-components";
import DotLegalFullScreenSpinner from "../../common/components/dotLegalFullScreenSpinner/DotLegalFullScreenSpinner";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import EmptyStateIcon from "../noCustomLists.svg?react";
import EditCustomListDialog from "../editCustomListDialog/EditCustomListDialog";
import { getTranslationKey } from "../customListOperations";
import { useUrlProvider } from "../../useUrlProvider";
import { CustomListDataRow } from "./CustomListView.types";
import ExportSvg from "../../common/components/dotLegalRecordHeader/export.svg?react";
import WhiteLockSvg from "../../common/svgs/whiteLock.svg?react";
import { PropertyType } from "../../common/PropertyTypes";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import PlanDialogButtonWrapper from "../../plan/planDialogWrapper/planDialogWrapper";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink";
import { exhaustiveGuard } from "../../common/utilities";

export interface ICustomListViewProps {}

function CustomListView(props: ICustomListViewProps) {
    const { dataProtectionFeatures, informationSecurityFeatures, addonsFeatures, vendorManagementFeatures } = usePlanContext();
    const { translateString, translatePropertyName } = useTranslation();
    const classes = useDotLegalTableStyles(2, false, false);
    const { getProcessingActivityOverviewUrl, getEditProcessingActivityUrl, getSystemOverviewUrl, getGroupEntityUrl, getLegalEntityUrl } = useUrlProvider();

    const {
        isLoading,
        isLoadingExport,
        columns,
        setSearchString,
        rows,
        onSortClick,
        orderBy,
        order,
        searchString,
        getBreadCrumbs,
        showEditDialog,
        setOpenEditDialog,
        customListId,
        refetch,
        listType,
        hasData,
        onClickExport,
        calculatedPage,
        count,
        from,
        noOfPages,
        to,
        setPage,
        isReadOnly,
        processingActivityLimitReached,
        processingActivityLimit,
    } = useCustomListViewHooks();

    const onClick = (row: CustomListDataRow) => {
        switch (listType) {
            case CustomListType.ProcessingActivity: {
                return getProcessingActivityOverviewUrl(row.id);
            }
            case CustomListType.Sharing: {
                return getEditProcessingActivityUrl(row.processingActivityId, 6, row.subStep, row.id);
            }
            case CustomListType.Company: {
                return row.isGroupCompany ? getGroupEntityUrl(row.id) : getLegalEntityUrl(row.id);
            }
            default: {
                new Error("ListType needs to be implemented: " + listType);
            }
        }
    };

    const getLinkStyle = (cell: any, index: number) => {
        const type = columns[index].columnType;

        const style = { fontWeight: "bold", fontStyle: "" };

        if (cell === translateString("unNamedSharing") && type === PropertyType.Sharings) {
            style.fontStyle = "italic";
        }

        return style;
    };

    const getTableCell = (cell: any, index: number) => {
        const type = columns[index].columnType;

        if (type == PropertyType.Contacts) {
            return (
                <Box component={"span"} sx={{ whiteSpace: "pre" }}>
                    {cell}
                </Box>
            );
        }

        return cell;
    };

    const exportBtnIsLocked = () => {
        switch (listType) {
            case CustomListType.ProcessingActivity: {
                return (isReadOnly && !dataProtectionFeatures.standardListExport) || (!isReadOnly && !addonsFeatures.customLists);
            }
            case CustomListType.Sharing: {
                return (isReadOnly && !dataProtectionFeatures.standardListExport) || (!isReadOnly && !addonsFeatures.customLists);
            }
            case CustomListType.Company: {
                return (isReadOnly && !vendorManagementFeatures.standardListsExport) || (!isReadOnly && !addonsFeatures.customLists);
            }
            default: {
                new Error("ListType needs to be implemented: " + listType);
            }
        }
    };

    const getWarningKey = () => {
        switch (listType) {
            case CustomListType.ProcessingActivity: {
                return "customListProcessingActivityLimitReached";
            }
            case CustomListType.Sharing: {
                return "customListSharingLimitReached";
            }
            case CustomListType.Company: {
                return "customListLimitReached";
            }
            default: {
                new Error("ListType needs to be implemented: " + listType);
            }
        }
    };

    const getUpgradeText = () => {
        switch (listType) {
            case CustomListType.ProcessingActivity:
            case CustomListType.Sharing: {
                return translateString("upgradeDataProtectionPlan");
            }
            case CustomListType.Company: {
                return translateString("upgradeVendorManagementPlan");
            }
            default: {
                exhaustiveGuard(listType);
            }
        }
    };

    const tooltipColumns = [PropertyType.Contacts];
    const showTooltip = (propertyType: PropertyType) => {
        return tooltipColumns.includes(propertyType);
    };

    const getTooltipText = (propertyType: PropertyType) => {
        if (tooltipColumns.includes(propertyType)) {
            let languageKey = getTranslationKey(propertyType);
            return translatePropertyName(languageKey + CustomListType[listType] + "ToolTip");
        }
        return "";
    };

    const renderHeaderButtons = () => (
        <Box sx={{ display: "flex", gap: 1 }}>
            {!isReadOnly && (
                <DotLegalButton buttonType="primary" size="medium" onClick={() => setOpenEditDialog("true")}>
                    {translateString("updateCustomList")}
                </DotLegalButton>
            )}
            <PlanDialogButtonWrapper text={getUpgradeText()} enabled={exportBtnIsLocked() ?? false}>
                <DotLegalButton
                    btnLength={120}
                    size="medium"
                    isLoading={isLoadingExport}
                    onClick={onClickExport}
                    buttonType={"primary"}
                    disabled={exportBtnIsLocked() || (columns.length === 0 && processingActivityLimitReached === false)}
                    endIcon={exportBtnIsLocked() ? <WhiteLockSvg /> : <ExportSvg />}
                    toolTip={exportBtnIsLocked() ? getUpgradeText() : undefined}
                >
                    {translateString("export")}
                </DotLegalButton>
            </PlanDialogButtonWrapper>
        </Box>
    );

    return (
        <React.Fragment>
            <DotLegalPageHeader
                isLoading={isLoading}
                breadCrumbs={getBreadCrumbs()}
                // The header builds its right component even while loading, and getUpgradeText throws until the list type is known.
                rightComponent={isLoading ? undefined : renderHeaderButtons()}
            />
            {isLoading ? (
                <DotLegalFullScreenSpinner />
            ) : (
                <>
                    <DotLegalPaper>
                        {hasData || processingActivityLimitReached ? (
                            <React.Fragment>
                                {processingActivityLimitReached && (
                                    <Box marginBottom={3}>
                                        <Alert
                                            severity="warning"
                                            sx={{
                                                "& .MuiSvgIcon-root": {
                                                    fill: "#ff9800",
                                                },
                                            }}
                                        >
                                            <AlertTitle>{translateString("warning")}</AlertTitle>
                                            {translateString(getWarningKey()!, { limit: processingActivityLimit })}
                                        </Alert>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <DotLegalSearchField labelSearch={translateString("search")} value={searchString} onChange={(e) => setSearchString(e)} />
                                </Box>
                                <TableContainer sx={classes.root}>
                                    <DotLegalPrettyScrollbar width={14}>
                                        <Table
                                            sx={[
                                                classes.dpaTable,
                                                {
                                                    "& tbody td:first-child, thead th:first-child": {
                                                        position: "sticky",
                                                        left: 0,
                                                        paddingLeft: 1,
                                                    },
                                                },
                                            ]}
                                            size={"small"}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    {columns.map((c, i) => (
                                                        <TableCell
                                                            key={c.columnType}
                                                            align={
                                                                (c.contentType === ContentType.Number || c.contentType === ContentType.Decimal) &&
                                                                c.columnType !== PropertyType.Status
                                                                    ? "right"
                                                                    : "left"
                                                            }
                                                        >
                                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                {c.columnType === PropertyType.Contacts ? (
                                                                    <Box sx={classes.headerText}>{translatePropertyName(getTranslationKey(c.columnType))}</Box>
                                                                ) : (
                                                                    <TableSortLabel
                                                                        sx={classes.headerText}
                                                                        active={i === orderBy}
                                                                        onClick={(_) => onSortClick(i)}
                                                                        direction={i === orderBy ? order : "asc"}
                                                                    >
                                                                        {translatePropertyName(getTranslationKey(c.columnType))}
                                                                    </TableSortLabel>
                                                                )}
                                                                {showTooltip(c.columnType) ? (
                                                                    <DotLegalInformationTooltip text={getTooltipText(c.columnType)} />
                                                                ) : undefined}
                                                            </Box>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.map((row, i) => (
                                                    <TableRow key={i}>
                                                        {row.data.map((cell: any, j: number) => {
                                                            return (
                                                                <TableCell key={j} align={typeof cell === "number" ? "right" : "left"}>
                                                                    {j === 0 ? (
                                                                        <DotLegalLink to={onClick(row)!}>
                                                                            <Box sx={getLinkStyle(cell, j)}>{cell}</Box>
                                                                        </DotLegalLink>
                                                                    ) : (
                                                                        getTableCell(cell, j)
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </DotLegalPrettyScrollbar>
                                    <DotLegalPagination
                                        calculatedPage={calculatedPage}
                                        count={count}
                                        from={from}
                                        noOfPages={noOfPages}
                                        setPage={setPage}
                                        to={to}
                                        hidePagination={false}
                                        labelOf={translateString("labelOf")}
                                    />
                                </TableContainer>
                            </React.Fragment>
                        ) : (
                            <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noListsFound")} />
                        )}
                    </DotLegalPaper>
                </>
            )}
            {!isLoading && showEditDialog && (
                <EditCustomListDialog
                    onSaveClick={() => {
                        setOpenEditDialog("");
                        refetch();
                    }}
                    onCloseClick={() => {
                        setOpenEditDialog("");
                        refetch();
                    }}
                    type={listType!}
                    listId={customListId}
                />
            )}
        </React.Fragment>
    );
}

export default CustomListView;
